import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table
} from "react-bootstrap";
import { rockTasksApi } from "../services/rockTasksApi";
import PaginationBar from "../components/common/PaginationBar";
import ErrorAlert from "../components/common/ErrorAlert";
import TaskDetailModal from "../components/tasks/TaskDetailModal";
import RockTaskV2DetailModal from "../components/tasks/RockTaskV2DetailModal";
import {
  normalizeIdentifyTaskResult,
  taskResultDisplayName,
} from "../utils/normalizeIdentifyTaskResult";

const ITEMS_PER_PAGE = 20;

const SOURCE_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "rock_identifier", label: "Rock Identifier" },
  { value: "openai", label: "OpenAI" },
  { value: "rockinapp", label: "Rockinapp" }
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "done", label: "Done" },
  { value: "failed", label: "Failed" }
];

const ENDPOINT_OPTIONS = [
  { value: "", label: "All endpoints" },
  { value: "recognize", label: "Recognize" },
  { value: "stream_message", label: "Stream Message" }
];

const PLATFORM_OPTIONS = [
  { value: "", label: "All platforms" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" }
];

const EMPTY_FILTER_OPTIONS = {
  app_versions: [],
  country_codes: [],
  language_codes: [],
  os_list: [],
  os_versions: []
};

const SOURCE_BADGE = {
  rock_identifier: "primary",
  openai: "info",
  rockinapp: "secondary"
};

export default function TaskManagerPage() {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: null, items_per_page: ITEMS_PER_PAGE, page: 1 });

  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedV2TaskId, setSelectedV2TaskId] = useState(null);

  const [taskIdInput, setTaskIdInput] = useState("");
  const [taskIdFilter, setTaskIdFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [vpnFilter, setVpnFilter] = useState("");
  const [endpointFilter, setEndpointFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [fallbackFilter, setFallbackFilter] = useState("");

  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appVersionFilter, setAppVersionFilter] = useState("");
  const [countryCodeFilter, setCountryCodeFilter] = useState("");
  const [languageCodeFilter, setLanguageCodeFilter] = useState("");
  const [deviceIdFilter, setDeviceIdFilter] = useState("");
  const [rcAppUserIdFilter, setRcAppUserIdFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [osFilter, setOsFilter] = useState("");
  const [osVersionFilter, setOsVersionFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const options = await rockTasksApi.getTaskFilters();
        if (!cancelled) setFilterOptions(options ?? EMPTY_FILTER_OPTIONS);
      } catch {
        if (!cancelled) setFilterOptions(EMPTY_FILTER_OPTIONS);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await rockTasksApi.getTasksList({
          page,
          items_per_page: ITEMS_PER_PAGE,
          task_id: taskIdFilter || undefined,
          source: sourceFilter || undefined,
          status: statusFilter || undefined,
          is_vpn: vpnFilter !== "" ? vpnFilter : undefined,
          endpoint: endpointFilter || undefined,
          platform: platformFilter || undefined,
          fallback_used: fallbackFilter !== "" ? fallbackFilter : undefined,
          from: dateFrom || undefined,
          to: dateTo || undefined,
          app_version: appVersionFilter || undefined,
          country_code: countryCodeFilter || undefined,
          language_code: languageCodeFilter || undefined,
          device_id: deviceIdFilter || undefined,
          rc_app_user_id: rcAppUserIdFilter || undefined,
          email: emailFilter || undefined,
          os: osFilter || undefined,
          os_version: osVersionFilter || undefined
        });
        if (!cancelled) {
          setItems(data?.items ?? []);
          setMeta({
            total: data?.objects?.total ?? null,
            items_per_page: ITEMS_PER_PAGE,
            page
          });
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [
    page,
    sourceFilter,
    statusFilter,
    taskIdFilter,
    vpnFilter,
    endpointFilter,
    platformFilter,
    fallbackFilter,
    dateFrom,
    dateTo,
    appVersionFilter,
    countryCodeFilter,
    languageCodeFilter,
    deviceIdFilter,
    rcAppUserIdFilter,
    emailFilter,
    osFilter,
    osVersionFilter
  ]);

  const totalPages = useMemo(() => {
    if (typeof meta.total === "number" && ITEMS_PER_PAGE > 0) {
      return Math.ceil(meta.total / ITEMS_PER_PAGE);
    }
    return null;
  }, [meta.total]);

  const canNext = useMemo(() => {
    if (totalPages !== null) return page < totalPages;
    return items.length >= ITEMS_PER_PAGE;
  }, [totalPages, page, items]);

  function handleSourceChange(e) {
    setPage(1);
    setSourceFilter(e.target.value);
  }

  function handleStatusChange(e) {
    setPage(1);
    setStatusFilter(e.target.value);
  }

  function handleVpnChange(e) {
    setPage(1);
    setVpnFilter(e.target.value);
  }

  function handleSimpleFilter(setter) {
    return (e) => {
      setPage(1);
      setter(e.target.value);
    };
  }

  function taskIsVpn(task) {
    return Boolean(task?.attributes?.is_vpn);
  }

  function renderVpnBadge(task) {
    return taskIsVpn(task) ? (
      <Badge bg="info" text="dark">VPN</Badge>
    ) : (
      <Badge bg="secondary">No</Badge>
    );
  }

  function handleTaskIdSearch(e) {
    e.preventDefault();
    setPage(1);
    setTaskIdFilter(taskIdInput.trim());
  }

  function taskRockName(task) {
    const normalized = normalizeIdentifyTaskResult(task);
    if (normalized.kind === "empty") return "—";
    return taskResultDisplayName(normalized);
  }

  function openTaskDetail(task) {
    if (!task) return;
    if (task.flow === "v2") {
      setSelectedV2TaskId(task.task_id);
      setSelectedTask(null);
    } else {
      setSelectedTask({ taskId: task.task_id, numericId: task.id });
      setSelectedV2TaskId(null);
    }
  }

  return (
    <div className="page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">Operations</div>
              <h2 className="page-title">Task Manager</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <ErrorAlert error={error} title="Failed to load tasks" />

          {/* Filters */}
          <Card className="mb-3">
            <Card.Body>
              <Form onSubmit={handleTaskIdSearch}>
                <Row className="g-2 align-items-end">
                  <Col xs={12} md={3}>
                    <Form.Label className="small text-muted mb-1">Source</Form.Label>
                    <Form.Select size="sm" value={sourceFilter} onChange={handleSourceChange}>
                      {SOURCE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Label className="small text-muted mb-1">Status</Form.Label>
                    <Form.Select size="sm" value={statusFilter} onChange={handleStatusChange}>
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Label className="small text-muted mb-1">Endpoint</Form.Label>
                    <Form.Select
                      size="sm"
                      value={endpointFilter}
                      onChange={handleSimpleFilter(setEndpointFilter)}
                    >
                      {ENDPOINT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Label className="small text-muted mb-1">VPN</Form.Label>
                    <Form.Select size="sm" value={vpnFilter} onChange={handleVpnChange}>
                      <option value="">Any</option>
                      <option value="true">VPN detected</option>
                      <option value="false">Not VPN</option>
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Label className="small text-muted mb-1">Task ID</Form.Label>
                    <InputGroup size="sm">
                      <Form.Control
                        placeholder="Search by task ID (partial match)..."
                        value={taskIdInput}
                        onChange={(e) => setTaskIdInput(e.target.value)}
                      />
                      <Button type="submit" variant="outline-secondary">
                        Search
                      </Button>
                      {taskIdFilter && (
                        <Button
                          variant="outline-danger"
                          onClick={() => {
                            setTaskIdInput("");
                            setTaskIdFilter("");
                            setPage(1);
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </InputGroup>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Label className="small text-muted mb-1">Platform</Form.Label>
                    <Form.Select
                      size="sm"
                      value={platformFilter}
                      onChange={handleSimpleFilter(setPlatformFilter)}
                    >
                      {PLATFORM_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Label className="small text-muted mb-1">Fallback</Form.Label>
                    <Form.Select
                      size="sm"
                      value={fallbackFilter}
                      onChange={handleSimpleFilter(setFallbackFilter)}
                    >
                      <option value="">Fallback — Any</option>
                      <option value="true">Fallback Yes</option>
                      <option value="false">Fallback No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Row className="g-2 mt-2 align-items-end">
                  <Col xs={12} md={3}>
                    <Form.Label className="small text-muted mb-1">App Version</Form.Label>
                    <Form.Select size="sm" value={appVersionFilter} onChange={handleSimpleFilter(setAppVersionFilter)}>
                      <option value="">All App Versions</option>
                      {(filterOptions.app_versions || []).map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={2}>
                    <Form.Label className="small text-muted mb-1">Country Code</Form.Label>
                    <Form.Select size="sm" value={countryCodeFilter} onChange={handleSimpleFilter(setCountryCodeFilter)}>
                      <option value="">All Countries</option>
                      {(filterOptions.country_codes || []).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={2}>
                    <Form.Label className="small text-muted mb-1">Language</Form.Label>
                    <Form.Select size="sm" value={languageCodeFilter} onChange={handleSimpleFilter(setLanguageCodeFilter)}>
                      <option value="">All Languages</option>
                      {(filterOptions.language_codes || []).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={2}>
                    <Form.Label className="small text-muted mb-1">OS</Form.Label>
                    <Form.Select size="sm" value={osFilter} onChange={handleSimpleFilter(setOsFilter)}>
                      <option value="">All OS</option>
                      {(filterOptions.os_list || []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={2}>
                    <Form.Label className="small text-muted mb-1">OS Version</Form.Label>
                    <Form.Select size="sm" value={osVersionFilter} onChange={handleSimpleFilter(setOsVersionFilter)}>
                      <option value="">All OS Versions</option>
                      {(filterOptions.os_versions || []).map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Label className="small text-muted mb-1">Device ID</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="Device ID..."
                      value={deviceIdFilter}
                      onChange={handleSimpleFilter(setDeviceIdFilter)}
                    />
                  </Col>
                </Row>

                <Row className="g-2 mt-2 align-items-end">
                  <Col xs={12} md={4}>
                    <Form.Label className="small text-muted mb-1">RC App User ID</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="RC app user ID..."
                      value={rcAppUserIdFilter}
                      onChange={handleSimpleFilter(setRcAppUserIdFilter)}
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Label className="small text-muted mb-1">Email</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="Email..."
                      value={emailFilter}
                      onChange={handleSimpleFilter(setEmailFilter)}
                    />
                  </Col>
                  <Col xs={12} md={2}>
                    <Form.Label className="small text-muted mb-1">Date From</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={dateFrom}
                      onChange={handleSimpleFilter(setDateFrom)}
                    />
                  </Col>
                  <Col xs={12} md={2}>
                    <Form.Label className="small text-muted mb-1">Date To</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={dateTo}
                      onChange={handleSimpleFilter(setDateTo)}
                    />
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {/* Table */}
          <Card>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="table-vcenter card-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>ID</th>
                      <th style={{ width: 120 }}>Task UUID</th>
                      <th style={{ width: 140 }}>Source</th>
                      <th style={{ width: 140 }}>Endpoint</th>
                      <th style={{ width: 100 }}>Status</th>
                      <th style={{ width: 80 }}>VPN</th>
                      <th>Rock Name</th>
                      <th style={{ width: 170 }}>Device</th>
                      <th style={{ width: 170 }}>Date</th>
                      <th style={{ width: 110 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={10} className="text-center py-4">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center text-muted py-4">
                          No tasks found.
                        </td>
                      </tr>
                    ) : (
                      items.map((task) => (
                        <tr key={task.task_id ?? task.id}>
                          <td>
                            <span className="fw-semibold">{task.id ?? "—"}</span>
                          </td>
                          <td>
                            <code className="small" title={task.task_id}>
                              {task.task_id ? `${task.task_id.slice(0, 8)}…` : "—"}
                            </code>
                          </td>
                          <td>
                            <Badge bg={SOURCE_BADGE[task.source] ?? "secondary"}>
                              {task.source}
                            </Badge>
                          </td>
                          <td>
                            {task.endpoint ? (
                              <Badge bg={task.endpoint === "estimate_price" ? "warning" : "primary"} text={task.endpoint === "estimate_price" ? "dark" : undefined}>
                                {task.endpoint}
                              </Badge>
                            ) : "—"}
                          </td>
                          <td>
                            <Badge
                              bg={
                                task.status === "done" || task.status === "completed"
                                  ? "success"
                                  : task.status === "processing"
                                    ? "warning"
                                    : "danger"
                              }
                              text={task.status === "processing" ? "dark" : undefined}
                            >
                              {task.status}
                            </Badge>
                            {task.flow === "v2" && (
                              <Badge bg="info" className="ms-1">
                                v2
                              </Badge>
                            )}
                          </td>
                          <td>{task.flow === "v2" ? renderVpnBadge(task) : "—"}</td>
                          <td>{taskRockName(task)}</td>
                          <td className="small text-muted">
                            {task.device_id ? (
                              <span
                                title={task.device_id}
                                style={{
                                  maxWidth: 160,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "block"
                                }}
                              >
                                {task.device_id}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="small text-muted">
                            {task.created_at
                              ? new Date(task.created_at).toLocaleString()
                              : "—"}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-info"
                              onClick={() => openTaskDetail(task)}
                              disabled={!task?.task_id}
                              title="View detail"
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer>
              <Row className="align-items-center gy-2">
                <Col>
                  <div className="text-muted small">
                    Page <b>{page}</b>
                    {typeof meta.total === "number" ? (
                      <>
                        {" "}· Total <b>{meta.total}</b>
                      </>
                    ) : null}
                  </div>
                </Col>
                <Col xs="auto">
                  <PaginationBar
                    page={page}
                    onPageChange={(p) => setPage(Math.max(1, p))}
                    canPrev={page > 1}
                    canNext={canNext}
                    showPageLabel={false}
                    totalPages={totalPages}
                  />
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </div>
      </div>

      <TaskDetailModal
        show={!!selectedTask?.taskId}
        onHide={() => setSelectedTask(null)}
        taskId={selectedTask?.taskId}
        taskNumericId={selectedTask?.numericId}
      />
      <RockTaskV2DetailModal
        show={!!selectedV2TaskId}
        onHide={() => setSelectedV2TaskId(null)}
        taskId={selectedV2TaskId}
      />
    </div>
  );
}
